import type {
  GenerateCodeRequest,
  LinkReferralRequest,
} from '@polaris/core/services'
import { queryOptions } from '@tanstack/react-query'
import { canCreateCode } from '@/src/services/referralcodes'
import {
  generateReferralCode,
  getReferrerProfile,
  linkReferralCode,
} from '@/src/services/referralcodes/authed'
import {
  REFERRAL_ERROR_MESSAGES,
  ReferralDisplayError,
  ReferralGenericError,
} from '@/src/utils/referral-errors'

const REFERRAL_CODES_ROOT_QUERY_KEY = 'referralcodes' as const

export const canCreateCodeQuery = (
  code: string,
  userId?: string,
  enabled = true,
) =>
  queryOptions({
    queryKey: [REFERRAL_CODES_ROOT_QUERY_KEY, 'can-create', code, userId],
    queryFn: () => canCreateCode(code, userId),
    enabled,
    staleTime: 5000,
    gcTime: 2000,
  })

export const generateCodeMutation = () => ({
  mutationKey: [REFERRAL_CODES_ROOT_QUERY_KEY, 'generate'],
  mutationFn: (request: GenerateCodeRequest) => generateReferralCode(request),
})

export const getReferrerProfileQuery = queryOptions({
  queryKey: [REFERRAL_CODES_ROOT_QUERY_KEY, 'referrer-profile'],
  queryFn: getReferrerProfile,
})

export const linkReferralCodeMutation = () => ({
  mutationKey: [REFERRAL_CODES_ROOT_QUERY_KEY, 'link'],
  mutationFn: async (request: LinkReferralRequest) => {
    const response = await linkReferralCode(request)

    // If the response has success: false and display_error, throw a display error
    if (!response.success && response.display_error) {
      throw new ReferralDisplayError(response.display_error)
    }

    // If the response has success: false but no display_error, throw a generic error
    if (!response.success) {
      throw new ReferralGenericError(
        REFERRAL_ERROR_MESSAGES.GENERIC_LINK_FAILED,
      )
    }

    return response
  },
  staleTime: 5000,
  gcTime: 2000,
})
